import { storageService } from '@/services/storage.service';
import { FileUploadResponse, UploadProgress } from '@/types/storage.types';
import TooltipArrow from '@/components/ui/TooltipArrow';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { CheckCircle2, Eye, Trash2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FormField from './FormField';

export interface FileUploadFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  currentFileUrl?: string;
  onFileChange: (fileUrl: string | null) => void;
  onRemove?: () => void;
  accept?: string;
  fileType?: 'image' | 'document' | 'any';
  maxSizeMB?: number;
  metadata?: Record<string, any>;
  className?: string;
}

/**
 * Reusable file upload component with drag & drop support
 * Features: progress tracking, preview, validation, and image optimization
 */
export default function FileUploadField({
  label,
  description,
  error,
  required = false,
  currentFileUrl,
  onFileChange,
  onRemove,
  accept = 'image/*',
  fileType = 'image',
  maxSizeMB = 10,
  metadata = {},
  className = '',
}: FileUploadFieldProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // Set when the <img> fails to load, so a non-loadable URL falls back to the
  // upload placeholder instead of showing a broken-image icon.
  const [imgError, setImgError] = useState(false);
  // Set once the preview image has finished loading — until then we show the upload UI
  // (never the Change/delete chrome) so a half-loaded/broken image never flashes.
  const [imgLoaded, setImgLoaded] = useState(false);

  // A stored URL only counts as a real file when it's a non-empty, meaningful value.
  // The course record can hand back '', 'null' or 'undefined' when no media was ever set.
  const storedUrl = (currentFileUrl || '').trim();
  const hasStoredUrl =
    storedUrl !== '' &&
    storedUrl.toLowerCase() !== 'null' &&
    storedUrl.toLowerCase() !== 'undefined';
  // Candidate file: a freshly-picked blob preview or a meaningful stored URL. The blob
  // preview is kept after upload so we don't have to re-fetch the S3 URL (which would
  // briefly show the upload UI again).
  const candidateUrl = previewUrl || (hasStoredUrl ? currentFileUrl : undefined);

  // Reset the load/error flags only when the image we display actually changes, so a
  // freshly-uploaded image (still shown via its blob preview) doesn't reload & flash.
  useEffect(() => {
    setImgError(false);
    setImgLoaded(false);
  }, [candidateUrl]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    // Validate file
    const validation = storageService.validateFile(file, { maxSizeMB });
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid file');
      return;
    }

    // Validate file type if image only
    if (fileType === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, GIF, WebP)');
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    }

    setFileName(file.name);
    setUploadComplete(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      const response: FileUploadResponse = await storageService.uploadFile(file, {
        onProgress: (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
        },
        metadata,
      });

      // Show completion state
      setUploadProgress(100);
      setUploadComplete(true);

      // Success - update with S3 URL after brief delay
      setTimeout(() => {
        onFileChange(response.url);
        toast.success('File uploaded successfully!');
        // Keep the blob preview so the just-uploaded image stays visible — re-fetching
        // the S3 url here would briefly drop back to the upload UI.
        setUploading(false);
        setUploadProgress(0);
        setUploadComplete(false);
      }, 800);
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Failed to upload file');
      setPreviewUrl(null);
      setFileName('');
      setUploading(false);
      setUploadProgress(0);
      setUploadComplete(false);
    }
  };

  const handleRemoveConfirmed = () => {
    setPreviewUrl(null);
    setFileName('');
    onFileChange(null);
    onRemove?.();
    setShowDeleteConfirm(false);
  };

  // Strip query string / hash before checking the extension — S3 / CDN URLs often
  // carry signed params (…image.png?X-Amz-…) or no extension at all.
  const fileExtMatchesImage = (url?: string) =>
    /\.(jpg|jpeg|png|gif|webp|svg|avif|bmp)$/i.test((url || '').split('?')[0].split('#')[0]);

  // Will we render this candidate as an image?
  const treatAsImage =
    !!candidateUrl && (!!previewUrl || fileType === 'image' || fileExtMatchesImage(currentFileUrl));

  // A freshly-picked blob preview is a known-good local image — show it immediately.
  // A STORED url is uncertain, so it must SUCCESSFULLY LOAD before we treat it as a
  // present file; until then we show the plain upload UI (never the Change/delete
  // chrome) so a broken or still-loading image can't flash the file-present state.
  const imageReady = treatAsImage && (!!previewUrl || (imgLoaded && !imgError));
  const imageLoading = treatAsImage && !previewUrl && !imgLoaded && !imgError;
  // Non-image documents have no <img> to load — present as soon as a URL exists.
  const documentReady = !!candidateUrl && !treatAsImage;

  // A confirmed, showable file drives the preview + Change/delete UI.
  const displayFile = imageReady || documentReady ? candidateUrl : undefined;
  const hasFile = !!displayFile;
  const isImage = imageReady;

  return (
    <FormField
      label={label}
      description={description}
      error={error}
      required={required}
      className={className}
    >
      <div className="space-y-3">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-xl transition-all duration-200 ${
            isDragging
              ? 'border-brand-blue-500 bg-brand-blue-50'
              : displayFile
              ? 'border-gray-200 bg-gray-50'
              : 'border-gray-300 hover:border-brand-blue-400 hover:bg-brand-blue-50/50'
          }`}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {/* Hidden detector: loads the candidate image off-screen so we only switch to
              the preview AFTER it has actually loaded. Until then the plain upload UI is
              shown — never the Change/delete chrome. */}
          {imageLoading && (
            <img
              src={candidateUrl}
              alt=""
              aria-hidden="true"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className="absolute h-0 w-0 opacity-0 pointer-events-none"
            />
          )}
          {displayFile && isImage ? (
            // Image Preview with improved visibility
            <div className="relative group p-6">
              <div className="relative max-w-full mx-auto">
                <img
                  src={displayFile}
                  alt="Preview"
                  onError={() => setImgError(true)}
                  className="w-full max-w-md h-auto max-h-64 object-contain rounded-lg border border-gray-300 bg-white shadow-sm mx-auto"
                />

                {/* Compact Upload Progress Overlay */}
                {uploading && (
                  <div className="absolute inset-0 bg-white/98 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <div className="w-full max-w-xs px-6">
                      {uploadComplete ? (
                        <div className="text-center space-y-3">
                          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-7 h-7 text-green-600" />
                          </div>
                          <p className="text-sm font-semibold text-green-700">Upload complete!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Compact spinner */}
                          <div className="w-12 h-12 border-3 border-brand-blue-200 border-t-brand-blue-600 rounded-full animate-spin mx-auto"></div>

                          {/* Compact progress info */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-medium text-gray-700">
                              <span className="truncate max-w-[180px]">{fileName}</span>
                              <span className="text-brand-blue-600">{Math.round(uploadProgress)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-brand-blue-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hover Actions */}
                {!uploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-3">
                      <TooltipArrow content="View full size" position="top">
                        <button
                          type="button"
                          onClick={() => setShowPreviewModal(true)}
                          className="p-3 bg-white rounded-xl shadow-lg hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="w-5 h-5 text-gray-700" />
                        </button>
                      </TooltipArrow>
                      <TooltipArrow content="Remove file" position="top">
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="p-3 bg-white rounded-xl shadow-lg hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </TooltipArrow>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : displayFile ? (
            // Non-image file preview
            <div className="p-6 text-center">
              <div className="text-gray-600 mb-2">
                <Upload className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-sm font-medium text-gray-700">{fileName || 'Uploaded file'}</p>
            </div>
          ) : imageLoading ? (
            // Loading a stored image — show a spinner (not the upload UI / not the
            // Change-delete chrome) until it loads or fails.
            <div className="p-8 flex flex-col items-center justify-center gap-3 min-h-[8rem]">
              <div className="w-10 h-10 border-3 border-brand-blue-200 border-t-brand-blue-600 rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500">Loading image…</p>
            </div>
          ) : (
            // Upload Placeholder
            <div className="p-8 text-center">
              <div
                className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-200 ${
                  isDragging ? 'bg-brand-blue-500 text-white scale-105' : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Upload className="w-6 h-6" />
              </div>

              <div className="space-y-1">
                <p className={`font-medium transition-colors ${isDragging ? 'text-brand-blue-600' : 'text-gray-900'}`}>
                  {isDragging ? 'Drop to upload' : 'Upload file'}
                </p>
                <p className="text-sm text-gray-500">
                  Drag & drop or{' '}
                  <span className="text-brand-blue-600 hover:text-brand-blue-700 font-medium cursor-pointer">browse</span>
                </p>
              </div>
            </div>
          )}

          {/* Hidden File Input */}
          {!uploading && (
            <input
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          )}
        </div>

        {/* Action Buttons */}
        {displayFile && !uploading && (
          <div className="flex gap-2">
            <TooltipArrow content="Upload a new file" position="top">
              <label className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-blue-500 text-white text-sm font-medium rounded-lg hover:bg-brand-blue-600 transition-colors cursor-pointer">
                <Upload className="w-4 h-4" />
                Change File
                <input type="file" accept={accept} onChange={handleFileSelect} className="hidden" />
              </label>
            </TooltipArrow>
            {isImage && (
              <TooltipArrow content="View full size" position="top">
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </TooltipArrow>
            )}
            <TooltipArrow content="Remove file" position="top">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </TooltipArrow>
          </div>
        )}
      </div>

      {/* Full Size Preview Modal */}
      {showPreviewModal && displayFile && isImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img
              src={displayFile}
              alt="Full size preview"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleRemoveConfirmed}
        title="Delete File"
        description="Are you sure you want to delete this file? This action cannot be undone."
        itemName={fileName || 'file'}
        isLoading={false}
      />
    </FormField>
  );
}
